import { useEffect, useRef, type RefObject } from 'react';

/**
 * Closes a dropdown/menu when the user mousedowns outside of `ref`.
 *
 * Extracted verbatim from the inlined effects in the `MultiSelectDropdown`
 * (components/RecurrenceSelector.tsx) and `RolesDropdown` (pages/TasksPage.tsx)
 * dropdowns, which both ran:
 *
 *   useEffect(() => {
 *     if (!open) return;
 *     const onDocMouseDown = (e: MouseEvent) => {
 *       if (ref.current && !ref.current.contains(e.target as Node)) {
 *         setOpen(false);
 *       }
 *     };
 *     document.addEventListener('mousedown', onDocMouseDown);
 *     return () => document.removeEventListener('mousedown', onDocMouseDown);
 *   }, [open]);
 *
 * Behaviour-preserving notes:
 *  - The listener is only attached while `enabled` is true (mirrors the early
 *    `if (!open) return;`), and is re-attached ONLY when `enabled` flips — the
 *    effect deps stay `[enabled]`. `handler` is read through a ref so a changing
 *    handler identity never re-registers the listener (the originals inlined the
 *    handler, so it was never a dependency).
 *  - Listens on `document` `mousedown`, same event/target as the originals.
 */
export function useOutsideClick(
  ref: RefObject<HTMLElement | null>,
  handler: () => void,
  enabled: boolean,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        handlerRef.current();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [enabled, ref]);
}
